import SpeakButton from "../EnglishTool/SpeakButton";

export const chapterPages = {
  1: [
    {
      title: <h2>Grade 5 Nouns: Review & Types</h2>,
      body: (
        <>
          <p>
            A <b>noun</b> names a person, place, thing, or idea.
            <SpeakButton text="A noun names a person, place, thing, or idea." />
          </p>
          <ul>
            <li>Common: <i>school, dog, river</i></li>
            <li>Proper: <i>Canada, Mr. Brown</i> <SpeakButton text="Canada, Mr. Brown" /></li>
            <li>Concrete: <i>flower, pencil</i></li>
            <li>Abstract: <i>courage, friendship</i></li>
          </ul>
        </>
      ),
    },
    {
      title: <h2>Compound & Collective Nouns</h2>,
      body: (
        <>
          <p>
            <b>Compound nouns:</b> Two words together (<i>sunflower, basketball</i>)
            <br/>
            <b>Collective nouns:</b> A group as one unit (<i>herd, swarm, audience</i>)
            <SpeakButton text="swarm, audience" />
          </p>
          <p>
            Example: “A <b>band</b> of musicians played at the festival.”
          </p>
        </>
      ),
    },
    {
      title: <h2>Possessive & Plural Nouns</h2>,
      body: (
        <>
          <p>
            <b>Possessive nouns:</b> Show ownership<br />
            <i>One dog's collar</i> (dog’s), <i>Two dogs’ collars</i> (dogs’)
            <SpeakButton text="dog's, dogs'" />
          </p>
          <p>
            Remember: Not all plurals end in “s” (e.g. <b>children, mice</b>)
          </p>
        </>
      ),
    },
    {
      title: <h2>Countable vs. Uncountable Nouns</h2>,
      body: (
        <>
          <p>
            <b>Countable:</b> You can count them (<i>tables, pencils</i>)
            <br />
            <b>Uncountable:</b> You cannot count them (<i>rice, advice, water</i>)
          </p>
          <p>
            We use “many”/“few” with countable, “much”/“little” with uncountable.
          </p>
        </>
      ),
    },
    {
      title: <h2>Noun Clauses</h2>,
      body: (
        <>
          <p>
            A <b>noun clause</b> acts as a noun in a sentence and often starts with <b>that, what, how, who, why</b>.<br />
            Example: <i>I know <b>that you are honest</b>.</i>
            <SpeakButton text="I know that you are honest." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Noun Skills</h2>,
      body: (
        <>
          <p>
            Find all compound, collective, and possessive nouns in the sentence:
            <br/>
            <b>
              “The team's coach collected everyone’s basketballs after practice.” 
              <SpeakButton text="The team's coach collected everyone's basketballs after practice." />
            </b>
          </p>
        </>
      ),
    },
  ],

  2: [
    {
      title: <h2>Grade 5 Verbs: Review & Verb Tenses</h2>,
      body: (
        <>
          <p>
            <b>Verbs</b> show action or state.<br />
            Grade 5 goes deeper into verb <b>tenses</b>!
          </p>
          <ul>
            <li>Simple Present: <i>He walks</i></li>
            <li>Present Perfect: <i>He has walked</i></li>
            <li>Past Perfect: <i>He had walked</i></li>
            <li>Future Perfect: <i>He will have walked</i></li>
          </ul>
          <SpeakButton text="He has walked. He had walked. He will have walked." />
        </>
      ),
    },
    {
      title: <h2>Perfect Tenses in Detail</h2>,
      body: (
        <>
          <p>
            <b>Present Perfect:</b> Action started in the past, continues or just finished. <br/>
            <i>She <b>has lived</b> here for five years.</i> <SpeakButton text="She has lived here for five years." />
          </p>
          <p>
            <b>Past Perfect:</b> Action finished before another past action.<br/>
            <i>She <b>had left</b> before I arrived.</i> <SpeakButton text="She had left before I arrived." />
          </p>
          <p>
            <b>Future Perfect:</b> Action that will finish before a point in the future.<br/>
            <i>She <b>will have finished</b> by 5 PM.</i> <SpeakButton text="She will have finished by five PM." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Modal Verbs</h2>,
      body: (
        <>
          <p>
            <b>Modal verbs</b> show ability, possibility, permission, or necessity.<br/>
            <b>Can, could, may, might, must, should, would</b>
          </p>
          <p>
            Example: <i>You <b>must</b> do your homework. <b>Could</b> you help me?</i>
            <SpeakButton text="You must do your homework. Could you help me?" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Irregular Verbs</h2>,
      body: (
        <>
          <p>
            Not all verbs add <b>-ed</b> for past tense!<br/>
            <b>Begin - began - begun</b>, <b>swim - swam - swum</b> <SpeakButton text="begin, began, begun. Swim, swam, swum." />
          </p>
          <p>
            Practice: Change “sing” to present, past, and past participle.
          </p>
        </>
      ),
    },
    {
      title: <h2>Verbals: Gerunds & Infinitives</h2>,
      body: (
        <>
          <p>
            <b>Gerund:</b> Verb + “-ing” used as a noun.<br />
            <i>Swimming is fun.</i>
          </p>
          <p>
            <b>Infinitive:</b> “to” + verb.<br />
            <i>I like to swim.</i>
            <SpeakButton text="Swimming is fun. I like to swim." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Verb Tenses, Modals & Verbals</h2>,
      body: (
        <>
          <p>
            Complete: <b>By tomorrow, I _____ (finish) my project.</b><br />
            <i>Type the answer using future perfect.</i>
          </p>
          <p>
            Write a sentence using “should” and a gerund.
          </p>
        </>
      ),
    },
  ],

  3: [
    {
      title: <h2>Grade 5 Adjectives: Review & Comparison</h2>,
      body: (
        <>
          <p>
            <b>Adjectives</b> describe nouns/pronouns. Comparison in Grade 5 is more complex!
            <br/>
            <i>old, older, oldest / beautiful, more beautiful, most beautiful</i>
          </p>
          <SpeakButton text="old, older, oldest; beautiful, more beautiful, most beautiful" />
        </>
      ),
    },
    {
      title: <h2>Order of Adjectives (Advanced)</h2>,
      body: (
        <>
          <p>
            Opinion → Size → Age → Shape → Color → Origin → Material → Purpose → Noun
          </p>
          <p>
            Example: <i>Five lovely small old round red Italian wooden tables</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Adjective Clauses</h2>,
      body: (
        <>
          <p>
            An <b>adjective clause</b> gives extra info about a noun.<br/>
            <b>Who, whom, whose, which, that</b> <SpeakButton text="Who, Whom, Whose, Which, That" />
            <br/>
            Example: “The teacher <b>who helped me</b> was kind.”
            <SpeakButton text="The teacher who helped me was kind." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Articles & Demonstratives</h2>,
      body: (
        <>
          <p>
            <b>Articles:</b> a, an, the<br />
            <b>Demonstratives:</b> this, that, these, those <SpeakButton text="This, that, these, those " />
          </p>
          <p>
            Example: “<b>This</b> book is mine. <b>Those</b> are yours.”
            <SpeakButton text="This book is mine. Those are yours." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Adjective Clauses & Order</h2>,
      body: (
        <>
          <p>
            Write a sentence using “which” or “that” as an adjective clause.
          </p>
          <p>
            Put these in order: <b>French / funny / young / actor</b> <br />
            <i>Answer: "funny young French actor"</i>
            <SpeakButton text="funny young French actor" />
          </p>
        </>
      ),
    },
  ],

  4: [
    {
      title: <h2>Grade 5 Adverbs: Review & Comparison</h2>,
      body: (
        <>
          <p>
            <b>Adverbs</b> can describe verbs, adjectives, or adverbs. Many Grade 5 adverbs are about degree, manner, and frequency.
            <SpeakButton text="Adverbs can describe verbs, adjectives, or adverbs." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Adverb Clauses</h2>,
      body: (
        <>
          <p>
            An <b>adverb clause</b> adds detail: <i>because, although, if, since, after</i> <SpeakButton text="because, although, if, since, after" />
          </p>
          <p>
            Example: <i>She stayed home <b>because she was sick</b>.</i>
            <SpeakButton text="She stayed home because she was sick." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Comparing Adverbs</h2>,
      body: (
        <>
          <p>
            <b>Comparative:</b> more quickly, better<br />
            <b>Superlative:</b> most quickly, best<br />
            Irregular: well, better, best
            <SpeakButton text="well, better, best" />
          </p>
        </>
      ),
    },
    {
      title: <h2>Placement & Types of Adverbs</h2>,
      body: (
        <>
          <p>
            <b>Adverbs of frequency:</b> always, usually, never
            <br />
            <b>Adverbs of degree:</b> very, almost, quite
          </p>
          <p>
            Example: “She almost always finishes her homework very quickly.”
            <SpeakButton text="She almost always finishes her homework very quickly." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Adverb Clauses & Types</h2>,
      body: (
        <>
          <p>
            Write a sentence with an adverb clause that starts with “if” or “although.”
          </p>
          <p>
            Identify the adverbs: <b>
              “He spoke softly but answered all questions quickly.”
              <SpeakButton text="He spoke softly but answered all questions quickly." />
            </b>
          </p>
        </>
      ),
    },
  ],

  5: [
    {
      title: <h2>Reading: Theme & Inference</h2>,
      body: (
        <>
          <p>
            <b>Theme:</b> The main message of a story.<br />
            <b>Inference:</b> Use clues to understand something not directly stated.
          </p>
          <p>
            Example: “Maya always brought extra food for her friend who sometimes forgot lunch.”
            <br />
            <i>What can you infer about Maya?</i>
            <SpeakButton text="Maya always brought extra food for her friend who sometimes forgot lunch." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Point of View</h2>,
      body: (
        <>
          <p>
            <b>First person:</b> I, we<br />
            <b>Second person:</b> you<br />
            <b>Third person:</b> he, she, they
          </p>
          <p>
            Example: “They packed their bags and left.”<br />
            <i>Which point of view is this?</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Citing Textual Evidence</h2>,
      body: (
        <>
          <p>
            To support your answer, <b>quote words or sentences</b> from the story.
          </p>
          <p>
            Example: <i>What sentence shows Max was excited?</i><br />
            “Max jumped up and down, clapping his hands.”
            <SpeakButton text="Max jumped up and down, clapping his hands." />
          </p>
        </>
      ),
    },
    {
      title: <h2>Context Clues & Vocabulary</h2>,
      body: (
        <>
          <p>
            Use <b>context clues</b> to figure out the meaning of new words.<br />
            Example: “The <b>arid</b> desert had very little water.”<br />
            <i>What does "arid" mean?</i>
          </p>
        </>
      ),
    },
    {
      title: <h2>Practice: Advanced Reading Skills</h2>,
      body: (
        <>
          <p>
            Read the passage:<br />
            <i>
              “During the storm, the lights flickered. Despite her fear, Emma calmly found the candles and helped her little brother.”
            </i>
            <SpeakButton text="During the storm, the lights flickered. Despite her fear, Emma calmly found the candles and helped her little brother." />
          </p>
          <ul>
            <li>What is the theme of this passage?</li>
            <li>What can you infer about Emma?</li>
            <li>Cite evidence that shows Emma is responsible.</li>
          </ul>
        </>
      ),
    },
  ],
};